import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Crown,
  Headphones,
  Code,
  Megaphone,
  Users,
  DollarSign,
  MessageSquare,
  Ticket as TicketIcon,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import {
  cn,
  formatBRL,
  formatDate,
  formatDateTime,
  formatRelative,
  orderStatusColor,
  priorityColor,
  priorityLabel,
} from '../lib/utils.js';

const roleConfig: Record<
  string,
  { label: string; color: string; icon: typeof Crown }
> = {
  founder: { label: 'Sócio', color: 'bg-purple-500/15 text-purple-300', icon: Crown },
  support_l1: { label: 'Suporte N1', color: 'bg-blue-500/15 text-blue-300', icon: Headphones },
  support_l2: { label: 'Suporte N2', color: 'bg-cyan-500/15 text-cyan-300', icon: Headphones },
  dev: { label: 'Dev', color: 'bg-emerald-500/15 text-emerald-300', icon: Code },
  growth: { label: 'Growth', color: 'bg-rose-500/15 text-rose-300', icon: Megaphone },
};

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function previousMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

function nextMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().slice(0, 7);
}

const commissionStatusColor: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-300',
  paid: 'bg-emerald-500/15 text-emerald-300',
  cancelled: 'bg-rose-500/15 text-rose-300',
  clawback: 'bg-orange-500/15 text-orange-300',
};

const ticketStatusColor: Record<string, string> = {
  open: 'bg-blue-500/15 text-blue-300',
  in_progress: 'bg-amber-500/15 text-amber-300',
  pending: 'bg-orange-500/15 text-orange-300',
  solved: 'bg-emerald-500/15 text-emerald-300',
  closed: 'bg-slate-500/15 text-slate-300',
};

export function EquipeDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [month, setMonth] = useState<string>(currentMonth());

  const { data, isLoading, error } = useQuery({
    queryKey: ['team-user', id, month],
    queryFn: () => apiClient.teamUserDetail(id!, month),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Carregando perfil...</div>;
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Link
          to="/equipe"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para Equipe
        </Link>
        <div className="text-rose-400">Usuário não encontrado.</div>
      </div>
    );
  }

  const { user, summary, commissions, recentMessages, tickets } = data;
  const role = roleConfig[user.role] ?? {
    label: user.role,
    color: 'bg-slate-500/15 text-slate-300',
    icon: Users,
  };
  const Icon = role.icon;
  const initials = user.fullName
    .split(' ')
    .filter((p) => p.length > 0)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/equipe"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Equipe
        </Link>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{user.fullName}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary text-xl font-bold">
            {initials}
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{user.fullName}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className={cn('text-[10px]', role.color)}>
                <Icon className="h-3 w-3 mr-1" />
                {role.label}
              </Badge>
              {user.regime && (
                <Badge variant="outline" className="text-[10px]">
                  {user.regime}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Mail className="h-3 w-3" /> {user.email}
              </span>
              {user.hireDate && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> desde {formatDate(user.hireDate)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-muted/40 rounded-md px-1 py-1 border border-border">
          <button
            onClick={() => setMonth(previousMonth(month))}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            ←
          </button>
          <div className="px-3 py-1 text-sm font-medium tabular-nums">{month}</div>
          <button
            onClick={() => setMonth(nextMonth(month))}
            disabled={month >= currentMonth()}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            →
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <DollarSign className="h-3.5 w-3.5" /> Earned no mês
            </div>
            <div className="text-2xl font-bold tabular-nums">
              {formatBRL(summary.earnedCents)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {summary.commissionsCount} comissões
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <Clock className="h-3.5 w-3.5" /> Pendente
            </div>
            <div className="text-2xl font-bold text-amber-400 tabular-nums">
              {formatBRL(summary.pendingCents)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {summary.pendingCount} a liberar
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <CheckCircle className="h-3.5 w-3.5" /> Pago
            </div>
            <div className="text-2xl font-bold text-emerald-400 tabular-nums">
              {formatBRL(summary.paidCents)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {summary.paidCount} liberadas
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <XCircle className="h-3.5 w-3.5" /> Canceladas
            </div>
            <div className="text-2xl font-bold text-rose-400 tabular-nums">
              {formatBRL(summary.cancelledCents)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {summary.cancelledCount} (refund/chargeback)
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commissions */}
      <Card>
        <CardHeader>
          <CardTitle>Comissões geradas no mês</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {commissions.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted-foreground">
              Sem comissões neste mês.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Data venda</th>
                    <th className="px-4 py-2 font-medium">Pedido</th>
                    <th className="px-4 py-2 font-medium">Cliente</th>
                    <th className="px-4 py-2 font-medium">Plano</th>
                    <th className="px-4 py-2 font-medium text-right">Base</th>
                    <th className="px-4 py-2 font-medium text-right">%</th>
                    <th className="px-4 py-2 font-medium text-right">Valor</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Liberação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {commissions.map((c) => (
                    <tr key={c.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground tabular-nums">
                        {formatDate(c.approvedAt ?? c.createdAt)}
                      </td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/vendas/${c.orderId}`}
                          className="text-sm font-medium hover:text-primary"
                        >
                          {c.productName}
                        </Link>
                        <div className="text-[10px] text-muted-foreground">
                          {formatBRL(c.amountGrossCents)} ·{' '}
                          <span
                            className={cn(
                              'inline-block px-1 rounded text-[9px]',
                              orderStatusColor[c.orderStatus],
                            )}
                          >
                            {c.orderStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs">{c.contactName ?? '—'}</td>
                      <td className="px-4 py-2.5 text-xs">
                        <code className="font-mono text-[10px] text-muted-foreground">
                          {c.planName}
                        </code>
                        <div className="text-[10px] text-muted-foreground">
                          base: {c.baseCalc}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {formatBRL(c.baseCents)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                        {(Number(c.percent) * 100).toFixed(1)}%
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
                        {formatBRL(c.amountCents)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium',
                            commissionStatusColor[c.status] ?? 'bg-slate-500/15',
                          )}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-[10px] text-muted-foreground tabular-nums">
                        {c.paidAt
                          ? `pago ${formatDate(c.paidAt)}`
                          : c.liberationDueDate
                          ? formatDate(c.liberationDueDate)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mensagens + Tickets */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              <MessageSquare className="inline h-4 w-4 mr-1" />
              Mensagens enviadas ({recentMessages.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-y-auto divide-y divide-border/40">
              {recentMessages.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  Nenhuma mensagem enviada.
                </div>
              ) : (
                recentMessages.map((m) => (
                  <div key={m.id} className="px-4 py-3 hover:bg-muted/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">
                        {m.contactName ?? '—'}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {formatRelative(m.createdAt)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-2">
                      {m.contentText}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      via {m.channel} · {m.messageType}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <TicketIcon className="inline h-4 w-4 mr-1" />
              Tickets atribuídos ({tickets.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-y-auto divide-y divide-border/40">
              {tickets.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  Nenhum ticket atribuído.
                </div>
              ) : (
                tickets.map((t) => (
                  <div key={t.id} className="px-4 py-3 hover:bg-muted/30">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-sm font-medium truncate flex-1">{t.subject}</div>
                      <span
                        className={cn(
                          'shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium',
                          ticketStatusColor[t.status] ?? 'bg-slate-500/15',
                        )}
                      >
                        {t.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      <Badge
                        variant="outline"
                        className={cn('text-[10px]', priorityColor[t.priority])}
                      >
                        {priorityLabel[t.priority] ?? t.priority}
                      </Badge>
                      <span>{t.type}</span>
                      <span>·</span>
                      <span>{formatDateTime(t.createdAt)}</span>
                      {t.csatScore !== null && t.csatScore !== undefined && (
                        <>
                          <span>·</span>
                          <span>CSAT {t.csatScore}/5</span>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {user.role === 'founder' && (
        <div className="text-xs text-muted-foreground border-t border-border pt-4">
          <strong>Profit share (Shadow Ledger):</strong> 50% do lucro líquido distribuído mensalmente.
          Em produção, conversão para distribuição de lucros formal quando CNPJ ativo (ver ADR 0004).
        </div>
      )}
    </div>
  );
}
